import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from 'lib/api';
import { getApiBaseUrl } from 'lib/api-base';

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role?: string;
  permissions?: string[];
}

interface AdminAuthContextType {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperadmin: boolean;
  hasPermission: (perm: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithVk: () => void;
  completeSession: (res: { user: AdminUser; token: string }) => void;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

function saveAdminAuth(res: { user: AdminUser; token: string }) {
  localStorage.setItem('admin_auth', JSON.stringify(res));
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession(): Promise<void> {
      const saved = localStorage.getItem('admin_auth');
      if (!saved) {
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }
      try {
        const parsed = JSON.parse(saved) as { user?: AdminUser; token?: string };
        if (!parsed.token) {
          localStorage.removeItem('admin_auth');
          if (!cancelled) {
            setIsLoading(false);
          }
          return;
        }
        const data = await api.getMe();
        if (cancelled) {
          return;
        }
        const nextUser: AdminUser = {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          permissions: data.user.permissions
        };
        setUser(nextUser);
        setToken(parsed.token);
        saveAdminAuth({ user: nextUser, token: parsed.token });
      } catch {
        localStorage.removeItem('admin_auth');
        if (!cancelled) {
          setUser(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    setUser(res.user);
    setToken(res.token);
    saveAdminAuth(res);
  }, []);

  const loginWithVk = useCallback(() => {
    const apiUrl = getApiBaseUrl();
    window.location.href = `${apiUrl}/api/auth/oauth/vk?target=dash`;
  }, []);

  const completeSession = useCallback((res: { user: AdminUser; token: string }) => {
    setUser(res.user);
    setToken(res.token);
    saveAdminAuth(res);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('admin_auth');
  }, []);

  const hasPermission = useCallback((perm: string) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    return (user.permissions || []).includes(perm);
  }, [user]);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        isSuperadmin: user?.role === 'superadmin',
        hasPermission,
        login,
        loginWithVk,
        completeSession,
        logout
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
}
